import ZelogIcon from 'assets/images/ZeLogLogo.svg';
import ZeLogMiniLogo from 'assets/images/ZeLogMiniLogo.svg';
import { useAuthStore } from 'features/auth/stores/authStore';
import {
  ChartColumn,
  Building2,
  FolderKanban,
  LayoutDashboard,
  LogOut,
  Settings,
  Timer,
  Users,
  PanelLeftOpen,
  PanelRightOpen,
} from 'lucide-react';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from 'shared/constants/routes';
import { usePermissions } from 'shared/hooks/usePermissions';

import {
  AppLogo,
  LogoutButton,
  NavItemButton,
  NavItemIcon,
  NavText,
  SidebarContainer,
  SidebarFooter,
  SidebarHeader,
  SidebarList,
  ToggleIconWrapper,
} from './Sidebar.styles';

export const SIDEBAR_WIDTH = 262;
export const SIDEBAR_COLLAPSED_WIDTH = 75;

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  const { logout } = useAuthStore();
  const permissions = usePermissions();

  const ICON_SIZE = 20;
  // prettier-ignore
  const navItems = [
    { label: 'Tracker',   path: ROUTE_PATHS.TRACKER,   icon: <Timer size={ICON_SIZE} />,           canView: true },
    { label: 'Projects',  path: ROUTE_PATHS.PROJECTS,  icon: <FolderKanban size={ICON_SIZE} />,       canView: true },
    { label: 'Clients',   path: ROUTE_PATHS.CLIENTS,   icon: <Building2 size={ICON_SIZE} />,        canView: permissions.canViewClients },
    { label: 'Team',      path: ROUTE_PATHS.TEAM,       icon: <Users size={ICON_SIZE} />,            canView: true },
    { label: 'Reports',   path: ROUTE_PATHS.REPORTS,   icon: <ChartColumn size={ICON_SIZE} />,        canView: true },
    { label: 'Dashboard', path: ROUTE_PATHS.DASHBOARD, icon: <LayoutDashboard size={ICON_SIZE} />, canView: true },
    { label: 'Settings',  path: ROUTE_PATHS.SETTINGS,  icon: <Settings size={ICON_SIZE} />,         canView: true },
  ].filter((item) => item.canView);

  const handleLogout = () => {
    logout();
    navigate(ROUTE_PATHS.LOGIN);
  };

  return (
    <SidebarContainer sidebarWidth={width}>
      <SidebarHeader collapsed={collapsed} onToggle={onToggle}>
        <AppLogo src={collapsed ? ZeLogMiniLogo : ZelogIcon} alt="ZeLog" />
        <ToggleIconWrapper onClick={onToggle}>
          {collapsed ? <PanelRightOpen size={18} /> : <PanelLeftOpen size={18} />}
        </ToggleIconWrapper>
      </SidebarHeader>
      <SidebarList collapsed={collapsed}>
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <NavItemButton
              key={item.path}
              selected={isActive}
              onClick={() => navigate(item.path)}
              collapsed={collapsed}
            >
              {/* Invisible <a> overlay — left-click blocked so NavItemButton handles navigation;
                  right-click exposes "Open link in new tab" from the browser context menu */}
              <a
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.preventDefault()}
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                }}
                aria-label={`Open ${item.label} in new tab`}
                tabIndex={-1}
              />
              <NavItemIcon collapsed={collapsed} isActive={isActive}>
                {item.icon}
              </NavItemIcon>

              {!collapsed && <NavText collapsed={collapsed} primary={item.label} />}
            </NavItemButton>
          );
        })}
      </SidebarList>

      <SidebarFooter collapsed={collapsed}>
        <LogoutButton collapsed={collapsed} onClick={handleLogout}>
          <NavItemIcon collapsed={collapsed} isActive={false}>
            <LogOut size={ICON_SIZE} />
          </NavItemIcon>
          {!collapsed && <NavText collapsed={collapsed} primary="Logout" />}
        </LogoutButton>
      </SidebarFooter>
    </SidebarContainer>
  );
};
